/**
 * The runs table (task 1306): one row per run — a Collaboration row IS a run —
 * newest first. The AI Service page's Runs tab, the Advanced endpoint hub's
 * Runs tab and the Config page's Collaborations tab all render it, so there is
 * one runs list, and every row opens the one run page,
 * `/services/:id/runs/:collaborationId`.
 *
 * Scope is an AI Service (`endpointId`) or a collaboration config
 * (`configId`). Every column reads the list response itself — the test that
 * produced the run, its turns, duration and cost come back on each row — so the
 * table makes no per-row reads; only the schedule attribution, which nothing on
 * a run records, comes from the service's schedule run logs (task 1003).
 *
 * The filters live in the URL (`?status=&tests=&from=&to=`), so a filtered
 * Runs tab is shareable. `tests` reads the way the Analytics tab writes it:
 * `0` leaves tests out, anything else (or nothing) keeps them in.
 *
 * Every row says whether a test produced it in its own Test column (task
 * 1333), and Started, Duration and Cost sort the whole filtered list on the
 * service (`?sort=started|duration|cost&dir=asc|desc` in the URL, next to the
 * filters; Started newest first when absent), because the list is paged.
 */
import { useState, type MouseEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { InboxIcon } from '@heroicons/react/24/outline';
import { useCollaborations } from '@/hooks/useCollaborations';
import { useScheduleAttribution, type ScheduleAttribution } from '@/hooks/useSchedules';
import { useFlag } from '@/hooks/useFeatureFlags';
import { formatRelativeTime } from '@/utils/dateUtils';
import { formatRunCost } from '@/utils/formatUtils';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/shared/EmptyState';
import { testRunSectionPath } from '@/components/layout/AssistantTestingCards';
import {
  CopyButton,
  formatClock,
  formatMs,
  getStatusBadgeClass,
  runErrorText,
} from '@/components/runExplorer/runExplorerShared';
import { serviceRunPath } from '@/components/runExplorer/runExplorerLinks';
import type { Collaboration } from '@/types';

interface ServiceRunsTableProps {
  /** The AI Service whose runs to list. */
  endpointId?: string;
  /** The collaboration config whose runs to list (the Config page). */
  configId?: string;
  /** The "No runs yet" action, e.g. the hub's Send Test Request. */
  onSwitchToTest?: () => void;
}

const PAGE_SIZE = 20;

/** The statuses a run moves through, in the filter's order. */
const STATUS_OPTIONS = ['completed', 'failed', 'running', 'pending', 'waiting_for_input'] as const;

/** The table's own URL params. */
const PARAM = { status: 'status', tests: 'tests', from: 'from', to: 'to', sort: 'sort', dir: 'dir' } as const;

/** The sortable columns, by URL value, and the list query's `sortBy` for each (task 1333). */
const SORT_BY = { started: 'startedAt', duration: 'duration', cost: 'cost' } as const;
type SortKey = keyof typeof SORT_BY;
type SortDir = 'asc' | 'desc';
const DEFAULT_SORT: { key: SortKey; dir: SortDir } = { key: 'started', dir: 'desc' };

const isSortKey = (value: string | null): value is SortKey => value !== null && value in SORT_BY;

/** Where a row opens: its AI Service's run page, else the run page with no service. */
const runPathOf = (run: Collaboration, scopeEndpointId: string | undefined): string => {
  const endpointId = run.endpointRequest?.endpointId ?? scopeEndpointId;
  return endpointId ? serviceRunPath(endpointId, run.id) : `/collaborations/${run.id}`;
};

/** The endpoint a row's Test chip opens the Testing tab of. */
const testEndpointOf = (run: Collaboration, scopeEndpointId: string | undefined): string | undefined =>
  run.endpointRequest?.endpointId ?? scopeEndpointId;

const stop = (event: MouseEvent) => event.stopPropagation();

/** The Test cell (task 1333): the scenario and test that produced the run, "—" for any other run. */
function TestCell({ run, scopeEndpointId }: { run: Collaboration; scopeEndpointId?: string }) {
  if (run.test) {
    const endpointId = testEndpointOf(run, scopeEndpointId);
    const label = `Test · ${run.test.scenarioName}${run.test.testNumber != null ? ` · #${run.test.testNumber}` : ''}`;
    const chipClass =
      'inline-flex max-w-[16rem] items-center truncate rounded-full bg-teal-100 px-2 py-0.5 text-xs font-medium text-teal-800 dark:bg-teal-900 dark:text-teal-200';
    return endpointId && run.test.testGroupId ? (
      <Link
        data-testid={`run-test-chip-${run.id}`}
        to={testRunSectionPath(endpointId, run.test.testGroupId)}
        onClick={stop}
        title={label}
        className={`${chipClass} hover:underline`}
      >
        {label}
      </Link>
    ) : (
      <span data-testid={`run-test-chip-${run.id}`} title={label} className={chipClass}>
        {label}
      </span>
    );
  }
  return <span className="text-sm text-gray-600 dark:text-gray-300">—</span>;
}

/** The Origin cell: where the run came from (an API call, a schedule, another run). */
function OriginCell({
  run,
  scheduledBy,
  scopeEndpointId,
}: {
  run: Collaboration;
  scheduledBy?: ScheduleAttribution;
  scopeEndpointId?: string;
}) {
  if (scheduledBy) {
    return (
      <span
        data-testid={`run-scheduled-${run.id}`}
        className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200"
      >
        Scheduled
        <Link
          to={`/services/${scopeEndpointId}?tab=config&section=schedules`}
          onClick={stop}
          className="font-medium underline"
        >
          by {scheduledBy.scheduleName}
        </Link>
      </span>
    );
  }
  // A run another run started through a tool call (the Assistant's
  // endpoint_invoke, a builder's smoke test): nothing on the row names which.
  if (run.parentCollaborationId) {
    return <span className="text-sm text-gray-600 dark:text-gray-300">Started by a run</span>;
  }
  return <span className="text-sm text-gray-600 dark:text-gray-300">{run.endpointRequest ? 'API call' : '—'}</span>;
}

export function ServiceRunsTable({ endpointId, configId, onSwitchToTest }: ServiceRunsTableProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const costEnabled = useFlag('innovate.fe.analytics.per-run-cost');

  const status = searchParams.get(PARAM.status) ?? '';
  const includeTests = searchParams.get(PARAM.tests) !== '0';
  const from = searchParams.get(PARAM.from) ?? '';
  const to = searchParams.get(PARAM.to) ?? '';
  const filtered = !!status || !includeTests || !!from || !!to;
  const sortParam = searchParams.get(PARAM.sort);
  // Cost sorts only where the Cost column shows
  const sortKey: SortKey = isSortKey(sortParam) && (sortParam !== 'cost' || costEnabled) ? sortParam : DEFAULT_SORT.key;
  const sortDir: SortDir = sortKey === sortParam && searchParams.get(PARAM.dir) === 'asc' ? 'asc' : DEFAULT_SORT.dir;
  const sorted = sortKey !== DEFAULT_SORT.key || sortDir !== DEFAULT_SORT.dir;

  const { data, isLoading, error } = useCollaborations(page, PAGE_SIZE, {
    ...(endpointId ? { endpointId } : {}),
    ...(configId ? { configId } : {}),
    ...(status ? { status } : {}),
    ...(includeTests ? {} : { includeTests: false }),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
    ...(sorted ? { sortBy: SORT_BY[sortKey], sortDir } : {}),
  });
  // Which runs a schedule produced (task 1003); free for a service with none.
  const scheduleAttribution = useScheduleAttribution(endpointId, { enabled: !!endpointId });

  const runs = data?.data ?? [];
  const pagination = data?.pagination;

  /** Write one filter to the URL (empty removes it) and go back to the first page. */
  const setFilter = (key: string, value: string) => {
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );
  };

  /**
   * Sort by a column: a second click on the active column flips its direction,
   * a new column starts descending. The default (Started, newest first) leaves
   * the URL; every change goes back to the first page.
   */
  const setSort = (key: SortKey) => {
    const dir: SortDir = key === sortKey ? (sortDir === 'desc' ? 'asc' : 'desc') : 'desc';
    setPage(1);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (key === DEFAULT_SORT.key && dir === DEFAULT_SORT.dir) {
          next.delete(PARAM.sort);
          next.delete(PARAM.dir);
        } else {
          next.set(PARAM.sort, key);
          next.set(PARAM.dir, dir);
        }
        return next;
      },
      { replace: true }
    );
  };

  /** A header that sorts the list, with `aria-sort` on the active one. */
  const sortHeader = (key: SortKey, label: string) => {
    const active = key === sortKey;
    return (
      <th
        className={headClass}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
        data-testid={`runs-sort-${key}`}
      >
        <button
          type="button"
          onClick={() => setSort(key)}
          className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-200 ${
            active ? 'text-gray-900 dark:text-white' : ''
          }`}
        >
          {label}
          <span aria-hidden="true" className={active ? '' : 'opacity-0'}>
            {active && sortDir === 'asc' ? '▲' : '▼'}
          </span>
        </button>
      </th>
    );
  };

  const inputClass =
    'rounded-md border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200';
  const headClass =
    'px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

  return (
    <div className="space-y-4" data-testid="service-runs-table">
      <div className="flex flex-wrap items-center gap-3" data-testid="runs-filters">
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          Status
          <select
            aria-label="Status"
            data-testid="runs-filter-status"
            value={status}
            onChange={(e) => setFilter(PARAM.status, e.target.value)}
            className={inputClass}
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          <input
            type="checkbox"
            data-testid="runs-filter-tests"
            checked={includeTests}
            onChange={(e) => setFilter(PARAM.tests, e.target.checked ? '' : '0')}
            className="h-4 w-4 rounded border-gray-300 text-indigo-600 dark:border-gray-600"
          />
          Include tests
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          From
          <input
            type="date"
            aria-label="From"
            data-testid="runs-filter-from"
            value={from}
            max={to || undefined}
            onChange={(e) => setFilter(PARAM.from, e.target.value)}
            className={inputClass}
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          To
          <input
            type="date"
            aria-label="To"
            data-testid="runs-filter-to"
            value={to}
            min={from || undefined}
            onChange={(e) => setFilter(PARAM.to, e.target.value)}
            className={inputClass}
          />
        </label>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Loading runs…</div>
      ) : error ? (
        <div className="py-8 text-center text-sm text-red-500 dark:text-red-400">The runs could not be loaded.</div>
      ) : runs.length === 0 ? (
        filtered ? (
          <EmptyState
            icon={<InboxIcon className="h-12 w-12" />}
            title="No runs match"
            description="No run matches these filters. Change or clear them to see more."
          />
        ) : (
          <EmptyState
            icon={<InboxIcon className="h-12 w-12" />}
            title="No runs yet"
            description="Nothing has run yet. Every call, test and scheduled run is listed here."
            action={onSwitchToTest ? { label: 'Send Test Request', onClick: onSwitchToTest, variant: 'primary' } : undefined}
          />
        )
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                {sortHeader('started', 'Started')}
                <th className={headClass}>Status</th>
                <th className={headClass}>Test</th>
                <th className={headClass}>Origin</th>
                <th className={headClass}>Environment</th>
                <th className={headClass}>Turns</th>
                {sortHeader('duration', 'Duration')}
                {costEnabled && sortHeader('cost', 'Cost')}
                <th className={headClass}>Run</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
              {runs.map((run) => {
                const path = runPathOf(run, endpointId);
                const scheduledBy = run.requestId ? scheduleAttribution.get(run.requestId) : undefined;
                return (
                  <tr
                    key={run.id}
                    data-testid={`run-row-${run.id}`}
                    onClick={() => navigate(path)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="whitespace-nowrap px-3 py-2 text-sm">
                      <Link
                        to={path}
                        onClick={stop}
                        className="font-medium text-gray-900 hover:text-indigo-600 dark:text-white dark:hover:text-indigo-400"
                      >
                        {formatClock(run.createdAt)}
                      </Link>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{formatRelativeTime(run.createdAt)}</div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusBadgeClass(run.status)}`}
                        title={run.error ? runErrorText(run.error) : undefined}
                      >
                        {run.status}
                      </span>
                    </td>
                    <td className="px-3 py-2" data-testid={`run-test-${run.id}`}>
                      <TestCell run={run} scopeEndpointId={endpointId} />
                    </td>
                    <td className="px-3 py-2">
                      <OriginCell run={run} scheduledBy={scheduledBy} scopeEndpointId={endpointId} />
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                      {run.environment ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                      {run.turnCount ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                      {formatMs(run.durationMs ?? null)}
                    </td>
                    {costEnabled && (
                      <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                        {run.cost ? formatRunCost(run.cost) : '—'}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1" onClick={stop}>
                        <span className="font-mono">{run.id.slice(0, 8)}</span>
                        <CopyButton text={run.id} label="Run ID" />
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <Pagination pagination={pagination} onPageChange={setPage} showPageSize={false} />
      )}
    </div>
  );
}
